import { useMutation } from "@apollo/client";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachFile from "@mui/icons-material/AttachFile";
import StopIcon from "@mui/icons-material/Stop";
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { gql } from "generated-graphql";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import type { ScoutFilePart } from "./message/scoutChatMessage";

import { uploadFileDirectly } from "../../../../util/uploadFile";
import { ScoutFileDisplay } from "./message/ScoutFileDisplay";

const INPUT_SCROLLBAR_WIDTH_PX = 12;
const INPUT_ACTION_OFFSET_PX = 20;
const INPUT_ACTION_RIGHT_PADDING_PX = 112;
const MAX_PDF_BYTES = 30 * 1024 * 1024;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT_BYTES = 200 * 1024;

const ATTACH_SCOUT_PRIVATE_DOCUMENT = gql(`
  mutation AttachScoutPrivateDocument($input: AttachScoutPrivateDocumentInput!) {
    attachScoutPrivateDocument(input: $input) {
      id
      title
      filename
      fileExtension
      mediaType
      sizeBytes
      ingestionMode
      extractionStatus
      signedDownloadUrl
    }
  }
`);

export type ScoutAttachment = {
  privateDocumentId: string;
  filename: string;
  title: string;
  mediaType: string;
  sizeBytes: number;
  signedDownloadUrl?: string;
};

type ScoutAttachmentError = {
  id: string;
  filename: string;
  mediaType: string;
  message: string;
};

const toMiB = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

function validateAttachmentFile(file: File): string | undefined {
  const mimeType = file.type.toLowerCase();

  if (mimeType === "application/pdf") {
    if (file.size > MAX_PDF_BYTES) {
      return `File is larger than 30 MB (${toMiB(file.size)}) and is not supported.`;
    }
    return undefined;
  }

  if (
    mimeType === "image/png" ||
    mimeType === "image/jpeg" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp"
  ) {
    if (file.size > MAX_IMAGE_BYTES) {
      return `File is larger than 5 MB (${toMiB(file.size)}).`;
    }
    return undefined;
  }

  if (mimeType === "text/plain") {
    if (file.size > MAX_TEXT_BYTES) {
      return "File is larger than 200 KB.";
    }
    return undefined;
  }

  return "File type is unsupported. Scout currently supports PDFs, images, and plain text.";
}

export const ScoutInput = memo(function ScoutThreadInput({
  onSubmit,
  onAbort,
  isSending,
  tenantId,
  conversationId,
  isAborting = false,
  placeholder = "Chat with Scout…",
  autoFocus = false,
}: {
  onSubmit: (message: string, attachments?: ScoutAttachment[]) => Promise<void>;
  onAbort?: () => void;
  isSending: boolean;
  tenantId?: string;
  conversationId?: string;
  isAborting?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const theme = useTheme();
  const [hasContent, setHasContent] = useState(false);
  const [attachments, setAttachments] = useState<ScoutAttachment[]>([]);
  const [attachmentErrors, setAttachmentErrors] = useState<ScoutAttachmentError[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachPrivateDocument] = useMutation(ATTACH_SCOUT_PRIVATE_DOCUMENT);
  const isSubmitDisabled = !hasContent || isUploading;
  const inputSurfaceColor = theme.palette.background.paper;
  const inputScrollbarThumbColor = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "light" ? 0.24 : 0.34
  );

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const clearInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setHasContent(false);
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setAttachmentErrors([]);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus, focusInput]);

  const uploadAttachments = useCallback(
    async (files: File[]) => {
      const nextErrors: ScoutAttachmentError[] = [];
      if (!tenantId) {
        setAttachmentErrors([
          {
            id: "missing-tenant",
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: "Scout requires a tenant before attaching files.",
          },
        ]);
        return;
      }
      if (files.length === 0) {
        return;
      }

      const validFiles: File[] = [];
      for (const file of files) {
        const error = validateAttachmentFile(file);
        if (error) {
          nextErrors.push({
            id: `${file.name}-${file.size}-${file.lastModified}-validation`,
            filename: file.name,
            mediaType: file.type || "application/octet-stream",
            message: error,
          });
        } else {
          validFiles.push(file);
        }
      }
      if (validFiles.length === 0) {
        setAttachmentErrors(nextErrors);
        return;
      }

      setAttachmentErrors([]);
      setIsUploading(true);
      try {
        const uploadedAttachments: ScoutAttachment[] = [];

        for (const file of validFiles) {
          try {
            const uploadKey = await uploadFileDirectly(file, { tenantId });
            const attachResult = await attachPrivateDocument({
              variables: {
                input: {
                  tenantId,
                  conversationId: conversationId ?? null,
                  uploadKey,
                  filename: file.name,
                  mediaType: file.type || "application/octet-stream",
                  sizeBytes: file.size,
                },
              },
            });
            const attached = attachResult.data?.attachScoutPrivateDocument;
            if (!attached) {
              throw new Error("Scout could not register this attachment.");
            }
            uploadedAttachments.push({
              privateDocumentId: attached.id,
              filename: file.name,
              title: file.name,
              mediaType: attached.mediaType,
              sizeBytes: attached.sizeBytes,
              signedDownloadUrl: attached.signedDownloadUrl ?? undefined,
            });
          } catch (error) {
            const message =
              error instanceof Error && error.message
                ? error.message
                : "Scout could not upload this file.";
            nextErrors.push({
              id: `${file.name}-${file.size}-${file.lastModified}-upload`,
              filename: file.name,
              mediaType: file.type || "application/octet-stream",
              message,
            });
          }
        }

        if (uploadedAttachments.length > 0) {
          setAttachments((previous) => [...previous, ...uploadedAttachments]);
        }
        setAttachmentErrors(nextErrors);
      } catch (error) {
        setAttachmentErrors([
          ...nextErrors,
          {
            id: `upload-failed-${Date.now()}`,
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: error instanceof Error ? error.message : "Scout could not upload files.",
          },
        ]);
      } finally {
        setIsUploading(false);
      }
    },
    [attachPrivateDocument, conversationId, tenantId]
  );

  const handleSubmit = useCallback(async () => {
    const value = inputRef.current?.value ?? "";
    if (!value.trim() || isSending) return;
    const attachmentPayload = attachments.length > 0 ? [...attachments] : undefined;
    clearInput();
    clearAttachments();
    focusInput();
    try {
      await onSubmit(value.trim(), attachmentPayload);
    } finally {
      focusInput();
    }
  }, [attachments, clearAttachments, clearInput, focusInput, isSending, onSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHasContent = !!e.target.value.trim();
    setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
  }, []);

  const handleRemoveAttachment = useCallback((privateDocumentId: string) => {
    setAttachments((previous) =>
      previous.filter((attachment) => attachment.privateDocumentId !== privateDocumentId)
    );
  }, []);

  const handleRemoveAttachmentError = useCallback((errorId: string) => {
    setAttachmentErrors((previous) => previous.filter((error) => error.id !== errorId));
  }, []);

  const handleFilePickerChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? [...event.target.files] : [];
      event.currentTarget.value = "";
      await uploadAttachments(files);
    },
    [uploadAttachments]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (isUploading || isSending) {
        return;
      }
      const files = [...event.dataTransfer.files];
      await uploadAttachments(files);
    },
    [isSending, isUploading, uploadAttachments]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (isSending && onAbort && !isAborting) {
          onAbort();
          return;
        }
        const textarea = inputRef.current;
        if (textarea && textarea.value.trim()) {
          clearInput();
          textarea.focus();
        } else if (textarea) {
          textarea.blur();
        }
      }
    },
    [handleSubmit, clearInput, isAborting, isSending, onAbort]
  );

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        px: 2,
        py: 1.25,
        borderRadius: (theme) => theme.spacing(3),
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/gif,image/webp,text/plain"
        multiple
        onChange={handleFilePickerChange}
        style={{ display: "none" }}
      />
      {attachments.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {attachments.map((attachment) => {
            const part: ScoutFilePart = {
              id: attachment.privateDocumentId,
              type: "file",
              title: attachment.title,
              filename: attachment.filename,
              mediaType: attachment.mediaType,
              url: `encamp-private-document://${attachment.privateDocumentId}`,
              document: attachment.signedDownloadUrl
                ? {
                    id: attachment.privateDocumentId,
                    signedDownloadUrl: attachment.signedDownloadUrl,
                  }
                : undefined,
            };
            return (
              <Stack key={attachment.privateDocumentId} direction="row">
                <Box sx={{ flex: 0.5 }}>
                  <ScoutFileDisplay
                    part={part}
                    onRemove={() => handleRemoveAttachment(attachment.privateDocumentId)}
                  />
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
      {attachmentErrors.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {attachmentErrors.map((attachmentError) => {
            const errorPart: ScoutFilePart = {
              id: attachmentError.id,
              type: "file",
              title: attachmentError.filename,
              filename: attachmentError.filename,
              mediaType: attachmentError.mediaType,
            };
            return (
              <Stack key={attachmentError.id} direction="row">
                <Box sx={{ flex: 0.5 }}>
                  <ScoutFileDisplay
                    part={errorPart}
                    state="error"
                    errorText={attachmentError.message}
                    onRemove={() => handleRemoveAttachmentError(attachmentError.id)}
                  />
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
      {isUploading ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Uploading file{attachments.length === 1 ? "" : "s"}...
        </Typography>
      ) : null}
      <TextField
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        variant="standard"
        slotProps={{
          input: { disableUnderline: true },
          htmlInput: { "aria-label": "Message Scout" },
        }}
        onChange={handleChange}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        sx={{
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
          },
          "& .MuiInputBase-inputMultiline": {
            padding: "4px 0",
            paddingRight: `${INPUT_ACTION_RIGHT_PADDING_PX}px`,
            minHeight: 24,
            lineHeight: 1.5,
            overflowX: "hidden",
            overflowY: "auto",
            scrollbarGutter: "stable",
            scrollbarWidth: "thin",
            scrollbarColor: `${inputScrollbarThumbColor} ${inputSurfaceColor}`,
            "&::-webkit-scrollbar": {
              width: `${INPUT_SCROLLBAR_WIDTH_PX}px`,
              backgroundColor: inputSurfaceColor,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: inputSurfaceColor,
              border: "none",
              boxShadow: "none",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: inputScrollbarThumbColor,
              border: "none",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: inputSurfaceColor,
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: `${INPUT_ACTION_OFFSET_PX}px`,
          bottom: 10,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {isSending && onAbort ? (
          <Tooltip title={isAborting ? "Stopping..." : "Stop generating (Esc)"} arrow>
            <span
              style={{
                display: "inline-flex",
                cursor: isAborting ? "default" : "pointer",
              }}
            >
              <IconButton
                onClick={onAbort}
                aria-label="Stop generating"
                color="primary"
                disabled={isAborting}
              >
                <StopIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Attach file" arrow>
              <span
                style={{
                  display: "inline-flex",
                  cursor: isUploading ? "default" : "pointer",
                }}
              >
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={isUploading}
                  aria-label="Attach file"
                  color="primary"
                >
                  <AttachFile sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Send (Enter · Shift+Enter for new line)" arrow>
              <span
                style={{
                  display: "inline-flex",
                  cursor: isSubmitDisabled ? "default" : "pointer",
                }}
              >
                <IconButton
                  onClick={handleSubmit}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={isSubmitDisabled}
                  aria-label="Send message"
                  color="primary"
                >
                  <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
});
ScoutInput.displayName = "ScoutThreadInput";
